import {
  IErrorApiResponse,
  IPaginationQuery,
  IPaginationResponse,
  ISuccessApiResponse,
  IValidationError,
} from '@wyz/types';
import {
  CrudOptions,
  OverridableQuery,
  UseMutationOptions,
  UseQueryOptions,
  restrictApi,
  serialize,
  useCrud,
  useMutation,
  useQuery,
} from '@wyz/utils';
import { isNil } from 'lodash-es';
import { useApi, useConfig } from '~/common/context';
import { CommonErrorCodesEnumType } from '~/common/types';
import { ConnectQueryKeys } from '~/connect/api';
import {
  CustomerCriterion,
  IRedistributionSupplierTemplate,
  IRedistributionSupplierTemplateFilterQuery,
  IRedistributionSupplierTemplatePayload,
} from '~/connect/types';

type Options = Pick<
  CrudOptions<
    IRedistributionSupplierTemplate,
    IRedistributionSupplierTemplatePayload
  >,
  'getOneById' | 'remove'
> & {
  create?: UseMutationOptions<
    ISuccessApiResponse,
    IErrorApiResponse<
      IValidationError<CommonErrorCodesEnumType>,
      CommonErrorCodesEnumType
    >,
    IRedistributionSupplierTemplatePayload
  >;
  update?: UseMutationOptions<
    ISuccessApiResponse,
    IErrorApiResponse<
      IValidationError<CommonErrorCodesEnumType>,
      CommonErrorCodesEnumType
    >,
    {
      id: string;
      body: IRedistributionSupplierTemplatePayload;
    }
  >;
  paginationFetcher?: {
    enabled?: boolean;
  };
  getMatched?: OverridableQuery<
    UseQueryOptions<
      ISuccessApiResponse<IRedistributionSupplierTemplate | null>,
      IErrorApiResponse<unknown, CommonErrorCodesEnumType>
    >
  > & {
    args?: {
      customerCriteria: Array<CustomerCriterion>;
      customerInternalCriteria: Array<CustomerCriterion>;
      parentId: string;
    };
  };
};

export const useManageRedistributionSupplierTemplates = <
  TRequestedKeys extends keyof Options,
  TRequestedOptions extends Options = Pick<Options, TRequestedKeys>,
>(
  options: TRequestedOptions,
) => {
  const { callCoreApi } = useApi();
  const { tenantCode } = useConfig();

  const api = useCrud<
    'getOneById' | 'remove',
    IRedistributionSupplierTemplate,
    IRedistributionSupplierTemplatePayload,
    IRedistributionSupplierTemplatePayload,
    IErrorApiResponse<IValidationError<CommonErrorCodesEnumType>>
  >(
    {
      baseKey: ConnectQueryKeys.redistributionSupplierTemplates.base(),
      caller: callCoreApi,
      endpoint: `/tenants/api/wtb/tenants/${tenantCode}/redistributions/supplier-templates`,
    },
    {
      getOneById: options.getOneById,
      remove: options.remove,
    },
  );

  const createMutation = useMutation({
    mutationFn: (variables) =>
      callCoreApi<ISuccessApiResponse>(
        `/tenants/api/wtb/tenants/${tenantCode}/redistributions/supplier-templates`,
        { method: 'POST', body: JSON.stringify(variables) },
      ),
    keysToInvalidateOnSuccess: [
      ConnectQueryKeys.redistributionSupplierTemplates.base(),
    ],
    retry: false,
    ...options.create,
  });

  const updateMutation = useMutation({
    mutationFn: (variables) =>
      callCoreApi<ISuccessApiResponse>(
        `/tenants/api/wtb/tenants/${tenantCode}/redistributions/supplier-templates/${variables.id}`,
        { method: 'PUT', body: JSON.stringify(variables.body) },
      ),
    keysToInvalidateOnSuccess: [
      ConnectQueryKeys.redistributionSupplierTemplates.base(),
    ],
    retry: false,
    ...options.update,
  });

  const paginationFetcher = (
    query: IPaginationQuery<IRedistributionSupplierTemplateFilterQuery>,
  ) =>
    callCoreApi<IPaginationResponse<IRedistributionSupplierTemplate>>(
      `/tenants/api/wtb/tenants/${tenantCode}/redistributions/supplier-templates?${serialize(
        {
          query: query.criteria.query,
          page: query.page,
          limit: query.limit,
        },
      )}`,
    );
  const getMatchedQuery = useQuery({
    queryKey: ConnectQueryKeys.redistributionSupplierTemplates.match(
      options.getMatched?.args,
    ),
    queryFn: () =>
      callCoreApi<ISuccessApiResponse<IRedistributionSupplierTemplate | null>>(
        `/tenants/api/wtb/tenants/${tenantCode}/redistributions/supplier-templates/customers/${options.getMatched?.args?.parentId}/matched?${serialize(
          options.getMatched?.args,
        )}`,
      ),
    ...options.getMatched,
    enabled:
      !isNil(options.getMatched?.args?.customerCriteria) &&
      Boolean(options.getMatched?.enabled) &&
      Boolean(options.getMatched?.args?.parentId),
    retry: false,
    gcTime: 0,
  });

  return restrictApi(options, {
    ...api,
    create: createMutation,
    update: updateMutation,
    paginationFetcher,
    getMatched: getMatchedQuery,
  });
};
